<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useVuelidate } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import { VImageUploader, VLoadingSection, VTextField } from '@/modules/shared/components'
import VSelect from '@/modules/investing/components/v-select.vue'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'

const props = defineProps<{
  individual: any
  onSubmit: (payload: any) => void
}>()

const individualStore = useIndividualStore()

const LINKEDIN_URL_REGEX = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/.+$/i

const currentIndividual = computed(() => props.individual)
const skeleton = computed(() => !currentIndividual.value)

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'JP', label: 'Japan' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'IN', label: 'India' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'KR', label: 'South Korea' },
  { value: 'IL', label: 'Israel' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'SE', label: 'Sweden' },
  { value: 'IE', label: 'Ireland' },
  { value: 'NZ', label: 'New Zealand' },
]

///////////////////////////////////////////////////////////////////////////////
// Form
///////////////////////////////////////////////////////////////////////////////

const form = ref({
  first_name: '',
  last_name: '',
  birthdate: '',
  country: '',
  linkedin_url: '',
})

const rules = {
  first_name: { required },
  last_name: { required },
  birthdate: { required },
  country: { required },
  linkedin_url: {
    linkedinUrl: helpers.withMessage(
      'Must be a valid LinkedIn profile URL (e.g. https://linkedin.com/in/username)',
      (value: string) => !value || LINKEDIN_URL_REGEX.test(value),
    ),
  },
}

const v$ = useVuelidate(rules, form, { $lazy: true })

const linkedinLookupLoading = ref(false)
const imageToDisplay = ref<string | null>(null)
const profilePictureFile = ref<File | null>(null)
const replaceProfilePicture = ref(false)
const linkedinPhotoUrl = ref<string | null>(null)

const onUpload = (file: File) => {
  profilePictureFile.value = file
  replaceProfilePicture.value = true
  linkedinPhotoUrl.value = null
}

const onRemove = () => {
  imageToDisplay.value = null
  profilePictureFile.value = null
  replaceProfilePicture.value = true
  linkedinPhotoUrl.value = null
}

const performLinkedinLookup = useDebounceFn(async (url: string) => {
  v$.value.linkedin_url.$reset()

  if (!LINKEDIN_URL_REGEX.test(url)) return

  const individualId = currentIndividual.value?.id
  if (!individualId) return

  linkedinLookupLoading.value = true
  try {
    const result = await individualStore.lookupLinkedin(individualId, url)
    if (result) {
      const [firstName, ...lastNameParts] = (result.name || '').split(' ')
      form.value.first_name = form.value.first_name || firstName
      form.value.last_name = form.value.last_name || lastNameParts.join(' ')
      form.value.country = result.country_code || form.value.country
      if (result.photo_url && !imageToDisplay.value) {
        imageToDisplay.value = result.photo_url
        linkedinPhotoUrl.value = result.photo_url
      }
    }
  } finally {
    linkedinLookupLoading.value = false
  }
}, 500)

watch(
  () => form.value.linkedin_url,
  (newUrl) => {
    if (newUrl) {
      performLinkedinLookup(newUrl)
    }
  },
)

const setForm = () => {
  const ind = currentIndividual.value
  if (!ind) return
  form.value.first_name = ind.first_name || ''
  form.value.last_name = ind.last_name || ''
  form.value.birthdate = ind.birthdate || ''
  form.value.country = ind.address?.country || ind.country || ''
  form.value.linkedin_url = ind.linkedin_url || ''
  imageToDisplay.value = ind.profile_picture || null
}

watch(
  currentIndividual,
  (value) => {
    if (value) setForm()
  },
  { immediate: true },
)

const isValid = async () => {
  return await v$.value.$validate()
}

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return
  const payload: any = {
    first_name: form.value.first_name,
    last_name: form.value.last_name,
    birthdate: form.value.birthdate,
    country: form.value.country,
    linkedin_url: form.value.linkedin_url,
  }
  if (replaceProfilePicture.value) {
    payload.profile_picture = profilePictureFile.value
    payload.replace_profile_picture = true
  } else if (linkedinPhotoUrl.value) {
    payload.photo_url = linkedinPhotoUrl.value
  }
  props.onSubmit(payload)
}

defineExpose({ isValid, submit })
</script>

<template>
  <div v-if="!skeleton">
    <div class="mb-6 flex justify-center">
      <VImageUploader :image="imageToDisplay" label="Photo" @upload="onUpload" @remove="onRemove" />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <VTextField v-model="form.first_name" label="First Name" placeholder="John" property="first_name" :v$="v$" />
      <VTextField v-model="form.last_name" label="Last Name" placeholder="Smith" property="last_name" :v$="v$" />
    </div>

    <div class="mt-4 grid grid-cols-2 gap-4">
      <VTextField v-model="form.birthdate" label="Date of Birth" type="date" property="birthdate" :v$="v$" />
      <VSelect
        v-model="form.country"
        label="Country"
        :options="COUNTRIES"
        placeholder="Select country..."
        property="country"
        :v$="v$"
      />
    </div>

    <div class="mt-4">
      <div class="relative">
        <VTextField
          v-model="form.linkedin_url"
          label="LinkedIn Profile"
          type="url"
          placeholder="https://linkedin.com/in/yourname"
          :disabled="linkedinLookupLoading"
          property="linkedin_url"
          :v$="v$"
          description="Visible on your investor profile"
        />
        <div v-if="linkedinLookupLoading" class="absolute right-3 top-[38px] -translate-y-1/2">
          <svg
            class="h-4 w-4 animate-spin text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>
    </div>
  </div>
  <VLoadingSection
    v-else
    class="my-40"
    title="Preparing Your Information"
    description="Please wait a moment while we set things up for you."
  />
</template>
